import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { ReactComponent as CloseIcon } from '../../../shared/icons/close-icon.svg';
import { ReactComponent as PlaceholdersIcon } from '../../../shared/icons/placeholders-icon.svg';
import { ReactComponent as ClauseTemplateIcon } from '../../../shared/icons/icon-clauses.svg';
import type { DocumentPermissions } from '../../../pages/document-wizard/shared/hooks/use-document-team-permissions/use-document-team-permissions';
import styles from './styles.module.scss';
import Tooltip from '../../tooltip';
import ClauseTemplateContainerForTemplates from '../document-editor/components/clauseTemplatesSidebar/clauseTemplateSidebarContainerForTemplates';
import { DocxNativePlaceholderPanel } from '../document-editor/docx-native/DocxNativePlaceholderPanel';
import { useDocumentState } from '../../../contexts/DocumentContext';

type DocumentActivitiesAndTemplatesNavigationProps = {
  isCollapsible: boolean;
  onClose: () => void;
  permissions: DocumentPermissions;
  teamId: string | null;
  isClauseTemplate?: boolean;
  // V4.0: docx-native templates read placeholders from the editor (registry),
  // not the CKEditor completeness query. documentId keys the registry lookup.
  isDocxNative?: boolean;
  documentId?: string;
};

enum ActiveSidenavPanel {
  PLACEHOLDERS = 'placeholders',
  CLAUSES = 'clauses',
}

const TemplateEditorSidenav = ({
  isCollapsible,
  onClose,
  permissions,
  teamId,
  isClauseTemplate = false,
  isDocxNative = false,
  documentId,
}: DocumentActivitiesAndTemplatesNavigationProps): JSX.Element => {
  const [activeSidenavPanel, setActiveSidenavPanel] = useState<ActiveSidenavPanel | null>(null);
  const [shouldShowToggleButton, setShouldShowToggleButton] = useState(false);
  const subNavRef = useRef(null);

  const { state: { restrictedEditingMode: isTemplateRestricted } } = useDocumentState();

  const setActiveSidenav = (activePanel: ActiveSidenavPanel | null) => {
    setActiveSidenavPanel(activeSidenavPanel === activePanel ? null : activePanel);
  };

  const toggleCollapse = () => {
    setActiveSidenavPanel(null);
  };

  const handleClose = () => {
    onClose && onClose();
  };

  return (
    <div className={classNames(styles.subNavMenuWrapper)} ref={subNavRef}>
      <div className={styles.documentNavigationBar}>
        <button
          type="button"
          onClick={handleClose}
          className={styles.closeButton}
          data-testid="document-header__close-cta"
        >
          <Tooltip
            testId="sidebar-button-activity"
            placement="bottom"
            icon={<CloseIcon className={styles.closeIcon} />}
          >
            Exit
          </Tooltip>
        </button>
        {!isClauseTemplate && (
          <div
            className={classNames(styles.documentNavigationBar__icon, {
              [styles.active]: isCollapsible && activeSidenavPanel === ActiveSidenavPanel.PLACEHOLDERS,
              [styles.enabled]: isCollapsible,
            })}
            onClick={() => setActiveSidenav(ActiveSidenavPanel.PLACEHOLDERS)}
          >
            <Tooltip testId="sidebar-button-team" placement="bottom" icon={<PlaceholdersIcon height="22" width="20" />}>
              <FormattedMessage id="nav.placeholders" defaultMessage="Placeholders" />
            </Tooltip>
          </div>
        )}
        {!isTemplateRestricted && (
          <div
            className={classNames(styles.documentNavigationBar__icon, {
              [styles.active]: isCollapsible && activeSidenavPanel === ActiveSidenavPanel.CLAUSES,
              [styles.enabled]: isCollapsible,
            })}
            onClick={() => setActiveSidenav(ActiveSidenavPanel.CLAUSES)}
          >
            <Tooltip
              testId="sidebar-button-clauses"
              placement="bottom"
              icon={<ClauseTemplateIcon height="22" width="20" />}
            >
              <FormattedMessage id="nav.clauses" defaultMessage="Clauses" />
            </Tooltip>
          </div>
        )}
      </div>
      {isCollapsible && (
        <div
          id="document-wizard-side-nav"
          className={classNames(styles.subNav, { [styles.collapsed]: !activeSidenavPanel })}
          aria-expanded={!activeSidenavPanel}
          onMouseEnter={() => setShouldShowToggleButton(true)}
          onMouseLeave={() => setShouldShowToggleButton(false)}
        >
          <button
            className={classNames(styles.subNavToggle, { [styles.subNavToggle__hidden]: !shouldShowToggleButton })}
            type="button"
            aria-expanded={!activeSidenavPanel}
            aria-controls="main-sub-nav"
            onClick={toggleCollapse}
          />
          <div className={styles.subNavMask}>
            <div className={styles.subNavContent}>
              {!isClauseTemplate && (
                <div style={{ display: `${activeSidenavPanel === ActiveSidenavPanel.PLACEHOLDERS ? 'block' : 'none'}` }}>
                  {/* The CKEditor placeholder sidebar that used to serve
                      non-docx templates went with CKEditor. */}
                  {documentId && <DocxNativePlaceholderPanel documentId={documentId} />}
                </div>
              )}
              {!isTemplateRestricted && (
                <div style={{ display: `${activeSidenavPanel === ActiveSidenavPanel.CLAUSES ? 'block' : 'none'}` }}>
                  <ClauseTemplateContainerForTemplates permissions={permissions} teamId={teamId} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateEditorSidenav;
